/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./Toast.module.css";
import { useUiStore } from "./stores/uiStore";

// ═══════════════════════════════════════════════════════════════════════════════
// Toast System — New DNA (amber accent, warm-ink shadow, Poppins)
// Usage: const { showToast, ToastContainer } = useToast();
//        showToast('Message', 'success');
//        showToast('Removed', 'success', { undo: true, onUndo: () => restore() });
//        showToast('Error', 'error');
// ═══════════════════════════════════════════════════════════════════════════════

let _toastId = 0;
let _globalShowToast = null;

// Global function — can be called from anywhere.
//
// ⚠ 2026-07-29 — this was a SILENT NO-OP almost everywhere, and that is why the
// Sell Sheets manual-image upload "died silently" through two hotfixes.
// _globalShowToast is registered only by useToast() below, and the only component
// that calls it is LensBankPage. So every one of the ~18 files importing this
// function — including useManagerReview / useQcDecisions (the whole point of
// Convention #107's hook-internal toast), useApplyMemoryUpdate, useApplyAllMemory,
// the Sales analytics surfaces and useMissingImages — threw its message into a
// null and showed the user nothing unless the Lens Bank happened to be open.
//
// FALLBACK (not a replacement): when no useToast() host is mounted, route to the
// uiStore toast, which App.js:500 renders unconditionally at app root. LensBankPage
// keeps its own richer host and is untouched; no container is ever double-rendered.
//
// Known degradation, deliberately accepted: the uiStore toast has no undo support,
// so `opts.undo` is dropped on the fallback path (affects useDevNumberLink's
// auto-link toasts). Those are currently 100% invisible, so message-without-undo is
// strictly better than silence. Restoring undo means extending the uiStore toast —
// a separate change to a global surface.
export function showToast(msg, type = "success", opts = {}) {
  if (_globalShowToast) { _globalShowToast(msg, type, opts); return; }
  try {
    // getState() is read lazily HERE, at call time — never at module init. The
    // import is safe to hoist: stores/uiStore imports only zustand, so there is no
    // cycle back to this file (verified 2026-07-29).
    // uiStore's signature is (msg, err: boolean); adapt at the boundary so none of
    // the 18 call sites has to change. 'warning' maps onto the ⚠ styling too.
    useUiStore.getState().showToast(msg, type === "error" || type === "warning");
  } catch (e) {
    // Never let a toast take down its caller — but never swallow it either.
    console.error("[toast] no host available; message dropped:", msg, e);
  }
}

export function useToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((msg, type = "success", opts = {}) => {
    const id = ++_toastId;
    const duration = opts.undo ? 5000 : 3000;
    setToasts(prev => [...prev, { id, msg, type, opts, entering: true }]);
    // Auto-remove after duration
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, 200);
    }, duration);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 200);
  }, []);

  // Register as global.
  //
  // The cleanup used to null the global UNCONDITIONALLY, which is a last-writer-wins
  // ownership bug: with two hosts mounted (or one remounting), the unmounting
  // component would clear a reference the surviving host still owned, silencing it
  // for the rest of the session and sending every module-scope showToast down the
  // fallback path. Only release the slot if we are still the one holding it.
  useEffect(() => {
    _globalShowToast = addToast;
    return () => { if (_globalShowToast === addToast) _globalShowToast = null; };
  }, [addToast]);

  return { showToast: addToast, removeToast, toasts };
}

// Semantic accent colors — New DNA palette (warm-ink family)
const ACCENT = {
  success: "#f5a523",
  warning: "#c4836a",
  error: "#c0503f",
};

const ICONS = {
  success: "✓",
  warning: "⚠",
  error: "✗",
};

export function ToastContainer({ toasts = [], removeToast }) {
  return (
    <div className={styles.toastRoot}>
      {toasts.map(t => (
        <div
          key={t.id}
          className={`${styles.toast}${t.exiting ? " " + styles.toastExiting : ""}`}
          style={{ "--accent": ACCENT[t.type] || ACCENT.success }}
        >
          <span className={styles.icon}>{ICONS[t.type] || ICONS.success}</span>
          <span className={styles.msg}>{t.msg}</span>
          {t.opts?.undo && (
            <button
              className={styles.undo}
              onClick={() => { if (t.opts.onUndo) t.opts.onUndo(); removeToast(t.id); }}
            >
              UNDO
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
